import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { setSidebarCollapsed } from '../actions/sidebar';
import media from '../util/media';
import TechExperience from '../components/TechExperience';

const MEASURE = '78ch';
const HEADING_COLOR = '#111';
const MUTED = '#6b7280';
const BORDER = 'rgba(0,0,0,0.08)';

const OuterContainer = styled.div`
  display: block;
  width: 100%;
`;

const Main = styled.main`
  max-width: ${MEASURE};
  margin: 0 auto;
  padding: 3rem 1.25rem 4rem;
  font-size: 18px;
  line-height: 1.6;
  padding-top: 150px;
`;

const Headline = styled.h1`
  margin: 0 0 1.5rem 0;
  color: ${HEADING_COLOR};
`;

const Intro = styled.section`
  margin-bottom: 0.75rem;

  p {
    margin: 0 0 1rem 0;
  }

  p:first-child {
    font-weight: 600;
  }
`;

const SectionNote = styled.p`
  font-size: 15px;
  color: ${MUTED};
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Engagements = styled.ul`
  padding: 0;
  list-style: disc outside;
`;

const EngagementItem = styled.li`
  margin-bottom: 0.85rem;

  strong {
    color: ${HEADING_COLOR};
    font-weight: 600;
  }
`;

const SectionHeading = styled.h2`
  margin: 3rem 0 0.75rem 0;
  color: ${HEADING_COLOR};
`;

const Role = styled.section`
  margin-top: 2rem;
  padding-top: 1.25rem;
  border-top: 1px solid ${BORDER};

  &.first {
    border-top: none;
    margin-top: 1rem;
    padding-top: 0;
  }

  p {
    margin-bottom: 0.75rem;
    font-size: 16.5px;
  }
`;

const RoleTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: ${HEADING_COLOR};
  margin-bottom: 0.2rem;
`;

const RoleMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.45rem;
  font-size: 14.5px;
  color: ${MUTED};
  margin-bottom: 0.9rem;
`;

const Period = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 500;
`;

const Dot = styled.span`
  color: rgba(0, 0, 0, 0.22);
`;

const Location = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 500;
`;

const EducationList = styled.ul`
  margin: 0.75rem 0 0 1.25rem;
  padding: 0;
  list-style: disc outside;
  li {
    font-size: 15.5px;
    margin-bottom: 0.5rem;
  }
`;

const ResponsiveWrapper = styled.div`
  /* phone adjustments */
  ${media.phone`
    ${Main} {
      padding-top: 100px;
    }
    ${Headline} {
      font-size: 34px;
    }
  `}
`;

class Experience extends React.PureComponent {
  componentDidMount() {
    this.props.dispatch(setSidebarCollapsed(true));
  }

  render() {
    return (
      <OuterContainer>
        <ResponsiveWrapper>
          <Main className="main-content">
            <Headline>Experience</Headline>

            <Intro className="intro">
              <p>
                I am a software engineer and entrepreneur with a decade of experience building
                secure, maintainable software systems. My practice emphasises technical
                judgement grounded in practical implementation: identifying high-leverage
                architectural choices early, reducing systemic risk, and delivering solutions
                teams can run at scale.
              </p>

              <p>
                Previously at Bynder and founder of multiple businesses. I now run a one-person
                consultancy and bring product perspective together with hands-on engineering to
                inform technical decisions that balance risk, velocity and long-term maintainability.
              </p>

              <p>
                In my spare time I like to tinker with electronics, embedded programming and home
                lab projects, while occasionally touching some grass.
              </p>

            <h4>Key technology experience includes</h4>
            <TechExperience />
            </Intro>

            <SectionHeading>Selected previous engagements</SectionHeading>
              <Engagements className="engagements">
                <EngagementItem>
                  <strong>Built and operated a distributed venture-capital platform processing more than half a million USD in retail investments</strong>
                  {' '}— distributed system architecture, asynchronous processing, consistency and financial transaction handling,
                  observability, and mentoring.
                </EngagementItem>

                <EngagementItem>
                  <strong>Built inventory management platform</strong>
                  {' '}— system architecture, mobile client and API development, third-party API integrations.
                </EngagementItem>

                <EngagementItem>
                  <strong>Designed, built, and operated a high-throughput survey-fulfilment system integrating with major survey providers</strong>
                  {' '}— system architecture, backend and batch processing, production operations, and technical leadership within the
                  product team.
                </EngagementItem>

                <EngagementItem>
                  <strong>Reviewed the architecture of a large e-commerce platform with 10,000+ sellers processing 20,000+ orders per day</strong>
                  {' '}— architecture review, technical risk assessment, and a prioritised remediation and migration roadmap.
                </EngagementItem>

                <EngagementItem>
                  <strong>Built native mobile clients for a financial-advisor platform serving 200,000+ users</strong>
                  {' '}— client-side architecture and implementation, secure integrations, offline resilience, and compatibility with the
                  existing platform.
                </EngagementItem>
              </Engagements>

              <SectionNote className="section-note">
	              Client work is confidential; the above selection is anonymised. If you'd like more detail,
	              case studies and references are available on request.
	            </SectionNote>

              <SectionHeading>Roles &amp; positions</SectionHeading>
              <Role className="role first">
                <RoleTitle>Software Engineer &amp; Founder, Code R</RoleTitle>
                <RoleMeta>
                  <Period>2024–present</Period>
                  <Dot>·</Dot>
                  <Location>The Randstad, Netherlands</Location>
                </RoleMeta>
                <p>
                  Transitioned Code R to a one-person consultancy, working directly with client teams to tackle engineering challenges,
                  make durable system design decisions, and provide technical direction. Client engagements range from short architecture
                  reviews and technical leadership to hands-on implementation and delivery.
                </p>
              </Role>

              <Role className="role">
                <RoleTitle>Co-founder &amp; Technical Lead, Code R</RoleTitle>
                <RoleMeta>
                  <Period>2017–2024</Period>
                  <Dot>·</Dot>
                  <Location>The Randstad, Netherlands</Location>
                </RoleMeta>
                <p>
                  Co-founded the company and led its technical direction while serving clients as a software engineer and architect.
                  Responsible for system architecture, core implementation, and engineering practices.
                </p>
              </Role>

              <Role className="role">
                <RoleTitle>Co-founder &amp; Technical Lead, Invyte</RoleTitle>
                <RoleMeta>
                  <Period>2017–2019</Period>
                  <Dot>·</Dot>
                  <Location>Rotterdam, Netherlands</Location>
                  <Dot>·</Dot>
                  <Location>Based at Bynder</Location>
                </RoleMeta>
                <p>
                  Responsible for system design, technical direction, and DevOps from an early stage. Contributed to securing early-stage VC funding.
                </p>
              </Role>

              <Role className="role">
                <RoleTitle>Graduate Intern, ICT Group</RoleTitle>
                <RoleMeta>
                  <Period>2017</Period>
                  <Dot>·</Dot>
                  <Location>Barendrecht, Netherlands</Location>
                </RoleMeta>
                <p>
                  Research and prototyping in distributed microservice infrastructure and orchestration to support rapid platform growth.
                </p>
              </Role>

              <Role className="role">
                <RoleTitle>Backend Developer, Bynder</RoleTitle>
                <RoleMeta>
                  <Period>2015–2016</Period>
                  <Dot>·</Dot>
                  <Location>Rotterdam, Netherlands</Location>
                </RoleMeta>
                <p>
                  Worked on the main Pyramid REST API on the backend team as the platform scaled through a 22 million USD series A and experienced 350% year-on-year growth.
                </p>
              </Role>

              <Role className="role education">
                <RoleTitle>BSc Software Engineering, Rotterdam University of Applied Sciences</RoleTitle>
                <RoleMeta>
                  <Location>Rotterdam, Netherlands</Location>
                  <Dot>·</Dot>
                  <Period>2013–2017</Period>
                </RoleMeta>
                <EducationList>
                  <li>Graduated with a thesis on infrastructure and system design.</li>
                  <li>Minor in software security.</li>
                  <li>Twice nominated for university software and innovation awards.</li>
                </EducationList>
              </Role>
          </Main>
        </ResponsiveWrapper>
      </OuterContainer>
    );
  }
}

export default connect()(Experience);
